import React, { useCallback, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useDidUpdate, useFormatDateHook } from "hooks";
import { useDateFormatSelect } from "store";

interface Props {
  defaultDateFrom?: string | null;
  defaultDateTo?: string | null;
  onSubmit: (data: { from: string; to: string }) => void;
  onClear: () => void;
}

export const DateRange: React.FC<Props> = ({ defaultDateFrom = null, defaultDateTo = null, onSubmit, onClear }) => {
  const [dateFrom, setDateFrom] = useState<string | null>(defaultDateFrom);
  const [dateTo, setDateTo] = useState<string | null>(defaultDateTo);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const formatDate = useFormatDateHook();

  useDidUpdate(() => {
    setDateFrom(defaultDateFrom);
  }, [defaultDateFrom]);

  useDidUpdate(() => {
    setDateTo(defaultDateTo);
  }, [defaultDateTo]);

  const defaultDisplayValue =
    defaultDateFrom && defaultDateTo
      ? `${formatDate({ date: defaultDateFrom })} - ${formatDate({ date: defaultDateTo })}`
      : "";

  const dateFormat = useDateFormatSelect();

  // Check if dates are valid and "To" is not before "From"
  const isDateRangeValid = dateFrom && dateTo && !dayjs(dateTo).isBefore(dayjs(dateFrom));

  const handleSubmit = useCallback(() => {
    setIsOpen(false);
    if (!dateFrom || !dateTo) return;

    // Validate that "To" date is not before "From" date
    if (dayjs(dateTo).isBefore(dayjs(dateFrom))) {
      return; // Don't submit if validation fails
    }

    onSubmit({ from: dateFrom, to: dateTo });
  }, [dateFrom, dateTo, onSubmit]);

  return (
    <>
      <TextField
        aria-readonly
        onClick={() => setIsOpen(true)}
        value={defaultDisplayValue}
        label="Effective Range"
        InputProps={{
          readOnly: true,
          startAdornment: <TodayOutlinedIcon />,
          ...(defaultDisplayValue && {
            endAdornment: (
              <IconButton
                onClick={(e) => {
                  // setDisplayValue("");
                  setDateFrom(null);
                  setDateTo(null);
                  onClear();
                  e.stopPropagation();
                }}
              >
                <CloseIcon />
              </IconButton>
            )
          })
        }}
      />
      <Dialog open={isOpen}>
        <DialogTitle>Date Range</DialogTitle>
        <DialogContent>
          <Stack gap={"20px"}>
            <Stack paddingTop="6px" direction="row" justifyContent="space-between" alignItems={"center"} gap={"14px"}>
              <DesktopDatePicker
                format={dateFormat}
                views={["year", "month", "day"]}
                maxDate={dateTo ? dayjs(dateTo) : undefined}
                slotProps={{
                  popper: {
                    sx: {
                      ".MuiPickersCalendarHeader-labelContainer": {
                        fontSize: "0.9rem"
                      }
                    }
                  },
                  textField: {
                    sx: { width: "180px", fontSize: "16px" }
                  }
                }}
                value={dateFrom ? dayjs(dateFrom) : null}
                label="From"
                onChange={(value) => {
                  if (value) {
                    setDateFrom(value.format("YYYY-MM-DD"));
                  }
                }}
              />
              <Divider orientation="horizontal" sx={{ width: "13px" }} />
              <DesktopDatePicker
                format={dateFormat}
                views={["year", "month", "day"]}
                minDate={dateFrom ? dayjs(dateFrom) : undefined}
                slotProps={{
                  popper: {
                    sx: {
                      ".MuiPickersCalendarHeader-labelContainer": {
                        fontSize: "0.9rem"
                      }
                    }
                  },
                  textField: {
                    sx: { width: "180px", fontSize: "16px" }
                  }
                }}
                value={dateTo ? dayjs(dateTo) : null}
                label="To"
                onChange={(value) => {
                  if (value) {
                    setDateTo(value.format("YYYY-MM-DD"));
                  }
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: "0 23px 23px" }}>
          <Button variant="outlined" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} color="primary" disabled={!isDateRangeValid}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
